<template>
  <div class="i-listview" ref="listview" aria-live="polite"
    @dragover.prevent
    @dragenter.prevent="(e) => dragenter(e)"
    @dragleave.prevent="(e) => dragleave(e)"
    @drop="(e) => drop(e)">
    <div v-show="!hasDocuments"
      role="region"
      aria-label="Document List"
      class="absolute inset-0 flex justify-center items-center">
      <EntityTableEmptyState v-if="!isNodeSelected">
        {{ LANG.clickOnCategory }}
      </EntityTableEmptyState>
      <Loading v-else-if="isPageLoading" />
      <EntityTableEmptyState v-else />
    </div>
    <div
      v-if="hasDocuments"
      class="i-listview-body"
      :style="{ overflow: dndEnabled && draggingFile ? 'hidden' : 'unset' }"
    >
      <table role="list">
        <colgroup>
          <col
              v-for="{name, width, maxWidth} in activeColumns"
              :key="name"
              :style="{
                minWidth: width ? `${width}px` : 'auto',
                width: (width || maxWidth) ? `${width || maxWidth}px` : 'auto',
              }"
          >
          <col key="spacer">
        </colgroup>
        <thead>
          <Draggable tag="tr" v-model="draggableColumns">
            <th
              v-for="{name, label, width, maxWidth, sortable} in activeColumns"
              :key="name"
              @click="() => sortColumn(name)"
              @keyup.enter="() => sortColumn(name)"
              :class="[
                `col__${name}`,
                { 'sortable' : sortable }
              ]"
              tabindex="0"
              :aria-sort="isSorted(name) ? (tableSortSetting.desc ? 'descending' : 'ascending') : null"
            >
              <i-checkbox
                v-if='name === "select"'
                :checked="isEveryDocumentSelected(documents)"
                @update:checked="() => selectAllDocuments(documents)"
              />
              <div v-else
                class="i-cell-value flex justify-between"
                :style="{ maxWidth: (maxWidth || width) ? `${maxWidth || width}px` : 'auto' }"
              >
                <span :style="{marginRight: '5px'}">{{columnHeader(name, label)}}</span>
                <i-sort-indicator
                  v-if="sortable"
                  :order="isSorted(name) ? (tableSortSetting.desc ? 'desc' : 'asc') : null"
                />
              </div>
            </th>
            <th key="spacer" class="spacer" :style="{width: 'auto'}"></th>
          </Draggable>

        </thead>
        <DocumentRowGroup
          v-for="(doc, idx) in documentsByNode"
          :key="doc[0].node_id"
          :document="doc[0]"
          :docIndex="idx"
          :viewport="viewport"
          @shiftSelect="(docIndex, selected) => shiftSelect(docIndex, selected)"
          v-bind="$attrs"
        />
        <tbody role="group">
          <LoaderBar v-show="isPageLoading"/>
        </tbody>
      </table>
    </div>
    <DragAndDropZone v-if="dndEnabled && draggingFile" @dragenter.prevent @dragover.prevent/>
    <ModalAssetUpload :droppedFiles="modalFiles" :category="this.selectedTreeNode" v-model:showModal="showModal"/>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { mapBrowserState, mapBrowserMutations, mapBrowserGetters, mapBrowserActions } from '@/store'; // eslint-disable-line
import { TreeMode, BrowserMode } from '@/store/modules/entity_browser/config';
import { VueDraggableNext as Draggable } from 'vue-draggable-next';
import DocumentRowGroup from '@/components/browser/documents/DocumentRowGroup.vue';
import DocumentTableSortIndicator from '@/components/browser/documents/DocumentTableSortIndicator.vue';
import DragAndDropZone from '@/components/browser/documents/DragAndDropZone.vue';
import ModalAssetUpload from '@/components/browser/ModalAssetUpload.vue';
import LoaderBar from '@/components/common/LoaderBar.vue';
import Loading from '@/components/common/Loading.vue';
import EntityTableEmptyState from '@/components/browser/documents/EntityTableEmptyState.vue';
import { Status } from '@/utils/useData';
import { showToast } from '@/utils/tools';

export default defineComponent({
  components: {
    // EntityRow,
    DocumentRowGroup,
    [DocumentTableSortIndicator.name]: DocumentTableSortIndicator,
    Draggable,
    LoaderBar,
    Loading,
    EntityTableEmptyState,
    DragAndDropZone,
    ModalAssetUpload,
  },
  data() {
    return {
      pagination: {
        page: 1,
        size: 200,
      },
      lastPageReached: false,
      lastScrollPosition: 0,
      viewport: {
        top: 0,
        bottom: 0,
      },
      draggingFile: false,
      dragCounter: 0,
      modalFiles: [],
      showModal: false,
    };
  },
  created() {
    this.refreshPage();
  },
  mounted() {
    const { listview } = this.$refs;
    const { scrollEvent } = this;
    let timeout;

    let ticking = false;

    listview.addEventListener('scroll', (e) => {
      if (!ticking) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          scrollEvent(e);
          ticking = false;
        }, 100);

        ticking = true;
      }
    });
  },
  watch: {
    documentQuery: {
      handler() {
        this.refreshPage();
      },
    },
    ftsFilterEnabled: {
      handler(value) {
        // Removing the fts filter
        // removes the user sort preference
        // going back to primary/relevance sort
        if (!value) {
          this.clearSortUserPreferance();
        }
      },
    },
  },
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapBrowserState([
      'isFiltersToolbarOpen',
      'treeMode',
      'isInsertMode',
      'isEntityTableDirty',
      'shiftSelectAnchor',
      'documentsStatus',
      'totalDocumentsCount',
    ]),
    ...mapBrowserGetters([
      'activeColumns',
      'documents',
      'documentsByNode',
      'query',
      'selectedTreeNode',
      'selectedTreeNodeObject',
      'selectedDocuments',
      'isEveryDocumentSelected',
      'isNodeSelected',
      'treeRecursiveModeGetter',
      'tableSortSetting',
      'ftsFilterEnabled',
    ]),
    ...mapGetters(['checkACL']),
    isPageLoading() {
      return this.documentsStatus === Status.LOADING;
    },
    documentQueryParams() {
      // eslint-disable-next-line object-curly-newline
      const { treeMode, browserMode, selectedTreeNode, treeRecursiveModeGetter, query, isEntityTableDirty, tableSortSetting } = this;

      const selectedNodeParam = {
        [TreeMode.CATEGORY]: 'cat_id',
        [TreeMode.DIRECTORY]: 'directory',
      }[treeMode];

      // FIXME: temporary fix to pass the correct node id
      // this should be removed once the end-point supports
      // using the full node id
      // eslint-disable-next-line
      const fixNodeId = (nodeId) => nodeId && nodeId != '/' ? nodeId.replaceAll(/^\/*/g, '') : nodeId;

      return {
        [selectedNodeParam]: fixNodeId(selectedTreeNode),
        recursive: treeRecursiveModeGetter ? 1 : 0,
        iql: query,
        only_assets: browserMode === BrowserMode.MAM ? 1 : 0,
        dirty: isEntityTableDirty ? 1 : 0,
        order_by: tableSortSetting.order,
        order_desc: tableSortSetting.desc,
      };
    },
    documentQuery() {
      // this simplifies 'watching' the query
      // by stringifying the query params, Vue won't `overshoot` the watcher
      // thus preventing unnecessary network requests
      return JSON.stringify(this.documentQueryParams);
    },
    paginatedQueryParams() {
      return {
        ...this.pagination,
        ...this.documentQueryParams,
      };
    },
    hasDocuments() {
      return this.documents.length && this.isNodeSelected;
    },
    draggableColumns: {
      get() {
        return this.activeColumns;
      },
      set(value) {
        value.forEach((el, idx) => {
          this.activeColumns[idx] = el;
          this.activeColumns[idx].order = idx;
        });
      },
    },
    dndEnabled() {
      return this.browserMode === BrowserMode.MAM
          && !!this.selectedTreeNode
          && !!this.selectedTreeNodeObject
          && !!this.selectedTreeNodeObject.can_create_documents
          && this.checkACL('/document/media/dnd_upload');
    },
  },
  methods: {
    ...mapBrowserActions([
      'fetchDocumentsPage',
      'saveColumnsState',
      'sortTable',
      'clearSortUserPreferance',
    ]),
    ...mapBrowserMutations([
      'clearDocuments',
      'toggleDocuments',
      'shiftSelectDocuments',
      'cleanEntityTable',
      'setShiftSelectAnchor',
    ]),
    async scrollEvent(event) {
      if (this.isPageLoading || this.lastPageReached) return;

      const listview = event.target;
      const { viewport } = this;

      viewport.top = listview.scrollTop;
      viewport.bottom = listview.scrollTop + listview.offsetHeight;

      const margin = 200;

      if (this.lastScrollPosition >= listview.scrollTop) return;

      this.lastScrollPosition = listview.scrollTop;

      if (listview.scrollTop + listview.offsetHeight + margin > listview.scrollHeight) {
        this.pagination.page += 1;
        const pageResponse = await this.fetchDocumentsPage(this.paginatedQueryParams);
        this.lastPageReached = !pageResponse;
      }
    },
    columnHeader(name, label) {
      if (name === 'preview_url' && this.isInsertMode) return this.LANG.insert;
      return label;
    },
    isSorted(columnName) {
      return this.tableSortSetting.order === columnName;
    },
    sortColumn(name) {
      const column = this.activeColumns.find((col) => col.name === name);
      if (!column || !column.sortable) return;

      let { order, desc } = this.tableSortSetting;
      if (this.isSorted(column.name)) {
        // Some math to toggle between 1 and 0 because perl doesn't have boolean
        desc = desc * -1 + 1;
      } else {
        order = column.name;
      }
      this.sortTable({ order, desc });

      this.refreshPage();
    },
    async refreshPage() {
      // Temporary fix for: ICMS-3735
      // TODO: Fix EntityTable data flow; refactor component
      if (!this.isNodeSelected) return;

      this.clearDocuments();

      this.lastPageReached = false;
      this.lastScrollPosition = 0;
      this.pagination.page = 1;

      await this.fetchDocumentsPage(this.paginatedQueryParams);
      this.cleanEntityTable();
    },
    drop(event) {
      event.preventDefault();
      if (!this.dndEnabled || !this.containsFiles(event)) return;
      this.dragCounter = 0;
      this.draggingFile = false;

      const { files } = event.dataTransfer;

      this.modalFiles = files;
      this.showModal = true;
    },
    dragenter(event) {
      if (!this.dndEnabled || !this.containsFiles(event)) return;
      this.dragCounter += 1;
      this.draggingFile = true;
    },
    dragleave(event) {
      if (!this.dndEnabled || !this.containsFiles(event)) return;
      this.dragCounter -= 1;
      if (this.dragCounter === 0) {
        this.draggingFile = false;
      }
    },
    containsFiles(event) {
      if (event.dataTransfer.types) {
        for (let i = 0; i < event.dataTransfer.types.length; i += 1) {
          if (event.dataTransfer.types[i] === 'Files') {
            return true;
          }
        }
      }

      return false;
    },
    shiftSelect(docIndex, selected) {
      let from = typeof this.shiftSelectAnchor === 'undefined' ? docIndex : this.shiftSelectAnchor;
      let to = docIndex;

      if (from > to) {
        [from, to] = [to, from];
      }

      this.shiftSelectDocuments({
        docs: this.documentsByNode.slice(from, to + 1).flat(),
        selected,
      });
      this.setShiftSelectAnchor(docIndex);
    },
    selectAllDocuments(documents) {
      if (!this.isEveryDocumentSelected(documents) && documents.length < this.totalDocumentsCount) {
        showToast(this.LANG.selectAllDocuments);
      }
      this.toggleDocuments(documents);
    },
  },
});
</script>

<style scoped lang='scss'>

.i-listview {
  overflow: auto;
  height: auto;
  margin-bottom: 50px;
  flex-grow: 1;
  user-select: none; /* CSS3 (little to no support) */
  -ms-user-select: none; /* IE 10+ */
  -moz-user-select: none; /* Gecko (Firefox) */
  -webkit-user-select: none; /* Webkit (Safari, Chrome) */

  .i-listview-header,
  .i-listview-body {
    overflow: unset;

    col {
      background: olivedrab;
    }
  }

  .i-listview-header {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .i-listview-header thead th.sortable {
    &:hover {
      cursor: pointer;
      background: $i-list-hover-bg;
    }
  }
}

table::v-deep {

  th, td {
    padding: 0 7px;
    white-space: nowrap;
    height: 45px;
    text-align: center;

    .i-cell-value {
      width: auto;
      text-align: center;
      padding: 0;

      .ac_link_cell {
        width: auto;
      }
    }

    &.col__filename,
    &.col__title,
    &.col__directory,
    &.col__url,
    &.col__category {
      text-align: left;
      .i-cell-value {
        text-align: left;
      }
    }
  }

  thead {
    background-color: #fff;
    color: $black;
    font-weight: normal;
    position: sticky;
    top: 0px;
    white-space: nowrap;
    box-shadow: 0px 3px $i-list-border-color;
    z-index: 10;
  }
  th {
    &.spacer {
      padding: 0;
      pointer-events: none;
    }

    &:hover {
      background-color: $i-list-hover-bg;
      cursor: pointer;
    }

    .i-cell-value {
      font-size: 13px;
      position: relative;
    }
  }

}

.column-resize {
  border-left: none;
  cursor: col-resize;
  height: 27px;
  margin: 0px;
  width: 3px;
  background: none $i-list-border-color;
  top: 6px;
  right: 0;
  opacity: 0.5;
}
</style>
